import { inject, injectable } from '@theia/core/shared/inversify';
import type { GitWorkspaceInfo } from '@lved/shared-core';
import { ITheiaProjectServiceServer } from './project-service-protocol';

export const TheiaProjectServiceFactory = Symbol('TheiaProjectServiceFactory');
export type TheiaProjectServiceFactory = () => ITheiaProjectService;

export const ITheiaProjectService = Symbol('ITheiaProjectService');
export type ITheiaProjectService = {
    generateProjectCode(projectPath: string): Promise<void>;
    compileProject(projectPath: string): Promise<void>;
    getGitRemotes(projectPath: string): Promise<GitWorkspaceInfo>;
    isGitInstalled(): Promise<boolean>;
};

/**
 * Frontend-facing project service used by Theia dependency injection.
 *
 * This class intentionally wraps the backend RPC server contract instead of
 * implementing project operations itself. UI code depends on this narrower
 * service while `ITheiaProjectServiceServer` remains the transport boundary
 * between the frontend and backend.
 */
@injectable()
export class TheiaProjectService implements ITheiaProjectService {
    constructor(@inject(ITheiaProjectServiceServer) private readonly server: ITheiaProjectServiceServer) {}

    async generateProjectCode(projectPath: string): Promise<void> {
        await this.server.generateProjectCode(projectPath);
    }

    async compileProject(projectPath: string): Promise<void> {
        await this.server.compileProject(projectPath);
    }

    async getGitRemotes(projectPath: string): Promise<GitWorkspaceInfo> {
        return this.server.getGitRemotes(projectPath);
    }

    async isGitInstalled(): Promise<boolean> {
        return this.server.isGitInstalled();
    }
}
